import { inject, Service } from '@angular/core';
import type {
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { FormArray } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ONYX_TOASTS_LIMIT, OnyxToastService } from '@onyx/angular';
import { chain } from 'lodash';
import type { UniqueField } from '../interfaces/utilities/unique-field';

@Service()
export class UniqueHelper {
  private readonly toastService = inject(OnyxToastService);
  private readonly translateService = inject(TranslateService);

  public handleDuplicates(fields: UniqueField[]): boolean {
    let errors = 0;
    for (const { values, key, paramKey } of fields) {
      const duplicates = UniqueHelper.getDuplicates(values);
      if (!duplicates.length) continue;

      this.toastService.showError({
        message: this.translateService.instant(paramKey, {
          [key]: duplicates.join(', '),
        }),
      });

      ++errors;
      if (errors >= ONYX_TOASTS_LIMIT) break;
    }

    return errors > 0;
  }

  public static getDuplicates = (values: string[]): string[] => {
    return chain(values)
      .groupBy((value) => value)
      .pickBy((group) => group.length > 1)
      .keys()
      .value();
  };

  public static createUniqueFieldValidator(
    fieldName: string,
    errorKey: string,
  ): ValidatorFn {
    return (formArray: AbstractControl): ValidationErrors | null => {
      if (!(formArray instanceof FormArray)) return null;

      const values = new Set<unknown>();
      for (const control of formArray.controls) {
        const fieldControl = control.get(fieldName);
        if (!fieldControl) continue;

        const value = fieldControl.value;
        if (!value) {
          UniqueHelper.clearControlError(fieldControl, errorKey);
          continue;
        }

        if (values.has(value)) {
          UniqueHelper.addControlError(fieldControl, errorKey);
        } else {
          values.add(value);
          UniqueHelper.clearControlError(fieldControl, errorKey);
        }
      }

      const hasDuplicate = formArray.controls.some((control) =>
        control.get(fieldName)?.hasError(errorKey),
      );
      return hasDuplicate ? { [errorKey]: true } : null;
    };
  }

  private static addControlError(
    control: AbstractControl,
    errorKey: string,
  ): void {
    const errors = control.errors ?? {};
    if (errors[errorKey]) return;

    control.setErrors({ ...errors, [errorKey]: true });
  }

  private static clearControlError(
    control: AbstractControl,
    errorKey: string,
  ): void {
    const errors = control.errors ?? {};
    if (!errors || !errors[errorKey]) return;

    if (Object.keys(errors).length === 1) {
      control.setErrors(null);
      return;
    }

    const { [errorKey]: _, ...remainingErrors } = errors;
    control.setErrors(remainingErrors);
  }
}
